/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import { styled } from "@mui/system";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Settings page components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";
import EditSettlement from "Modals/EditSettlementNewOutrights";

// sweetalert2 components
import Swal from "sweetalert2";

// react-router components
import { getData, postData } from "Utils/server";
import { getAuthorizationHeader } from "Utils/helpers";
import { useSoftUIController } from "context";

import { Alert, Icon, Snackbar } from "@mui/material";

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

let settlements = [];
let uncheckedFixtures = {};
let checkedFixtures = {};
let timeExceededRules = {
  soccer: 2.5,
  tabletennis: 1.5,
};

const DEFAULT_HIDDEN_COLUMNS = ["tm", "selected", "checkedOdds", "uncheckedOdds", "diffsOdds"];

const SORT_BY_UNCHECKED_STORAGE_KEY = 'outrights_settlement_sort_by_unchecked';

function getInitialSortByUncheckedValue() {
  if (typeof window === 'undefined') return false;

  try {
    const rememberedSortByUnchecked = window.localStorage.getItem(SORT_BY_UNCHECKED_STORAGE_KEY);

    if (rememberedSortByUnchecked === null) return false;

    return rememberedSortByUnchecked === 'true';
  } catch (error) {
    console.error('Unable to read outrights settlement sort preference', error);
    return false;
  }
}

let oddSettingsEditCached = true;
let currentData = [];
let allTableData = [];

function OutrightSettlement() {
  const abortControllerRef = useRef(new AbortController());

  const [controller] = useSoftUIController();
  const { providers, permissions, sports } = controller;
  const [showEditModal, setEditModal] = useState(false);
  const [rowDataSelected, setRowDataSelected] = useState(null);
  const [sortByUnchecked, setSortByUnChecked] = useState(getInitialSortByUncheckedValue);
  const [workflow, setWorkflow] = useState(undefined);
  const [settlementManager, setSettlementManager] = useState(false);
  const [loading, setLoading] = useState(false);

  const [autoRefresh, setAutoRefresh] = useState(true);

  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "tm", accessor: "tm", width: "100px" },
      { Header: "selected", accessor: "selected", width: "100px" },
      { Header: "CheckedDiffs", accessor: "checkedOdds", width: "80px", Cell: checkedDiffs },
      { Header: "UnCheckedDiffs", accessor: "uncheckedOdds", width: "80px", Cell: uncheckedDiffs },
      { Header: "Diffs", accessor: "diffsOdds", width: "80px" },
      { Header: "odds", accessor: "odds_count", width: "80px", Cell: formatOddsCell },

      {
        Header: "Sport - Region - Competition - Event - Market",
        accessor: "outrightName",
        width: "80px",
      },
      { Header: "Status", accessor: "status", width: "100px", Cell: formatStatus },
      { Header: "note", accessor: "note", width: "80px", Cell: editNotes },

      { Header: "provider/ Uni id", accessor: "fixture_id", width: "100px", Cell: matchedRender },
      { Header: "Start Date", accessor: "start_date", width: "100px", Cell: renderDate },
    ],

    rows: [],
  });

  const [hiddenColumns, setHiddenColumns] = useState(DEFAULT_HIDDEN_COLUMNS);
  const [selectedRows, setSelectedRows] = useState([]);
  const [notification, setNotification] = useState(false);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");
  const [, setUpdateEnabled] = useState(false);
  const [mutedFixtures, setMutedFixtures] = useState(null);
  const [unmutedFixtures, setUnmutedFixtures] = useState(null);
  const [mutedSelected, setMutedSelected] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [outrightSelected, setOutrightSelected] = useState(false);
  const [fixturesLoaded, setFixturesLoaded] = useState(false);
  const [mutedFixturesLoaded, setMutedFixturesLoaded] = useState(false);

  const statusSorting = {
    Finished: 0,
    "Coverage lost": 1,
    PreFinished: 1,
    Suspended: 2,
    "In progress": 3,
    "Not started yet": 4,
  };

  const getSportName = (item) => {
    if (item?.sport) return item.sport;
    if (!item?.sport_id || !sports || sports.length === 0) return "Outrights";

    const sportSelected = sports.find((sportItem) => sportItem.id == item.sport_id);
    return sportSelected ? sportSelected.name : "Outrights";
  };

  const mapFixtureForOddsModal = (item) => {
    if (!item) return null;

    const providerId = item.providerId || item.provider_id || 1000;
    const providerName = item.provider_name || item.provider;
    const ids =
      item.ids && item.ids.length > 0
        ? item.ids
        : item.event_id
        ? [item.event_id]
        : item.fixture_id
        ? [item.fixture_id]
        : [];

    const providersList =
      item.providers && item.providers.length > 0
        ? item.providers
        : providerName
        ? [providerName]
        : [];

    const eventIds =
      item.eventIds && item.eventIds.length > 0
        ? item.eventIds
        : item.event_id
        ? [item.event_id]
        : item.fixture_id
        ? [item.fixture_id]
        : [];

    return {
      ...item,
      sport: getSportName(item),
      providerId,
      ids,
      providers: providersList,
      eventIds,
      away: item.away || "",
      start_date_utc: item.start_date_utc || item.start_date,
      finished_at_utc: item.finished_at_utc || item.finished_at,
    };
  };

  const handleEditOpenModal = (e, rowData) => {
    e.preventDefault();

    const selectedItem = mapFixtureForOddsModal(rowData?.row?.original);
    if (!selectedItem) return;

    setRowDataSelected(selectedItem);
    setEditModal(true);
  };

  const handleEditCloseModal = async (update) => {
    setRowDataSelected(null);
    setEditModal(false);

    if (update && autoRefresh) {
      fetchFixtures(true);
    }
  };

  function formatOddsCell({ value, ...rest }) {
    return (
      <div className="cell-width">
        <Button
          onClick={(e) => handleEditOpenModal(e, rest)}
          sx={{
            minWidth: "unset",
            padding: "0px",
            textAlign: "left",
            textTransform: "none",
          }}
        >
          <SoftTypography
            component="span"
            variant="button"
            fontWeight="regular"
            color="info"
            opacity={0.8}
          >
            {value}
          </SoftTypography>
        </Button>
      </div>
    );
  }

  function editNotes({ ...rest }) {
    return (
      <div style={{ display: "grid" }}>
        <TextField
          className={"multilineTextField noteHolder"}
          id="outlined-multiline-flexible"
          label="Notes"
          multiline
          maxRows={3}
          value={rest.row.original.note || ""}
          onChange={(e) => setNoteData(e, rest.row)}
        />
        <Button
          className={"sync-notes-button"}
          onClick={(e) => sentNotes(e, rest.row.original.latest_note_id)}
        >
          sync Notes
        </Button>
      </div>
    );
  }

  function setNoteData(e, row) {
    e.preventDefault();

    row.original.note = e.currentTarget.value;
    setDataTableData((previousData) => ({
      ...previousData,
      rows: [...(currentData.rows || [])],
    }));
  }

  async function sentNotes(e, latest_note_id) {
    e.preventDefault();

    let noteToSend = currentData.rows.find((item) => item.latest_note_id == latest_note_id);

    let setOddNoteEndPoint = `/api/v1/bets/odd/note`;
    let response = await postData(
      setOddNoteEndPoint,
      { id: noteToSend.latest_note_id, note: noteToSend.note },
      {
        Authorization: getAuthorizationHeader(),
      }
    );

    if (response != null) {
      setApiresponse("success");
      setApiResponseMsg("Note succesfully updated!");
      setNotification(true);
    } else {
      setApiresponse("error");
      setApiResponseMsg("Something went wrong! Note doesn't updated. Please try again later. ");
      setNotification(true);
    }
  }

  function setSortBy() {
    setSortByUnChecked((previousValue) => !previousValue);
  }

  function setAutorefreshValue() {
    setAutoRefresh((previousValue) => !previousValue);
  }

  function changeSorting(data) {
    const dataTable = data ? { ...data } : { ...dataTableData };
    const dataRows = Array.isArray(dataTable.rows) ? [...dataTable.rows] : [];

    if (sortByUnchecked) {
      dataRows.sort((a, b) => {
        if (a.already_settled || b.already_settled) {
          return sortingByAlreadySettled(a, b);
        }

        if (a.status === "Finished" && b.status === "Finished") {
          return sortingByFinishtDate(a, b);
        }
        return sortingByNewStartDate(a, b);
      });
    } else {
      dataRows.sort((a, b) => {
        if (a.already_settled || b.already_settled) {
          return sortingByAlreadySettled(a, b);
        }

        if (a.status === "Finished" && b.status === "Finished") {
          return sortingByFinishtDate(a, b);
        }

        return sortingByStartDate(a, b);
      });
    }

    const nextData = { ...dataTable, rows: dataRows };
    currentData = nextData;
    setHiddenColumns(DEFAULT_HIDDEN_COLUMNS);
    setDataTableData(nextData);
  }

  function sortingByAlreadySettled(a, b) {
    const sorting = Number(b.already_settled) - Number(a.already_settled);
    return sorting;
  }

  function sortingByStartDate(a, b) {
    let sorting = sortByStatus(a, b);
    if (sorting != 0) return sorting;
    sorting =
      a.start_date - b.start_date == 0
        ? parseInt(a.fixture_id) - parseInt(b.fixture_id) !== 0
          ? sortBy(a.fixture_id, b.fixture_id)
          : sortBy(a.odd_name, b.odd_name)
        : sortBy(a.start_date, b.start_date);

    return sorting;
  }

  function getTimestampFromDateString(dateString) {
    if (!dateString || typeof dateString !== "string") return 0;

    const parsedValue = dateString
      .trim()
      .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/);

    if (!parsedValue) return 0;

    const day = Number(parsedValue[1]);
    const month = Number(parsedValue[2]);
    const year = Number(parsedValue[3]);
    const hour = Number(parsedValue[4]);
    const minute = Number(parsedValue[5]);

    if (
      Number.isNaN(day) ||
      Number.isNaN(month) ||
      Number.isNaN(year) ||
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return 0;
    }

    const date = new Date(year, month - 1, day, hour, minute, 0, 0);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day ||
      date.getHours() !== hour ||
      date.getMinutes() !== minute
    ) {
      return 0;
    }

    return date.getTime();
  }

  function sortingByNewStartDate(a, b) {
    let sorting = sortByStatus(a, b);
    if (sorting != 0) return sorting;
    let fixture_a_timestamp = getTimestampFromDateString(a.note);
    let fixture_b_timestamp = getTimestampFromDateString(b.note);
    if (fixture_a_timestamp == 0) fixture_a_timestamp = parseInt(a.start_date);
    if (fixture_b_timestamp == 0) fixture_b_timestamp = parseInt(b.start_date);
    sorting =
      fixture_a_timestamp - fixture_b_timestamp == 0
        ? parseInt(a.fixture_id) - parseInt(b.fixture_id) !== 0
          ? sortBy(a.fixture_id, b.fixture_id)
          : sortBy(a.odd_name, b.odd_name)
        : sortBy(fixture_a_timestamp, fixture_b_timestamp);

    return sorting;
  }

  function sortingByFinishtDate(a, b) {
    let sorting = sortByStatus(a, b);
    if (sorting != 0) return sorting;
    sorting =
      a.finished_at_utc - b.finished_at_utc == 0
        ? parseInt(a.fixture_id) - parseInt(b.fixture_id) !== 0
          ? sortBy(a.fixture_id, b.fixture_id)
          : sortBy(a.odd_name, b.odd_name)
        : sortBy(a.finished_at_utc, b.finished_at_utc);

    return sorting;
  }

  function sortByStatus(a, b) {
    if (statusSorting[a.status] < statusSorting[b.status]) {
      return -1;
    }
    if (statusSorting[a.status] > statusSorting[b.status]) {
      return 1;
    }
    return 0;
  }

  function sortBy(a, b) {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  }

  const handleTableDataChanges = async ({ key, value, extra }) => {
    if (key == "refresh") {
      fetchFixtures(true);
    }

    if (key == "selectOne") {
      setUpdateEnabled(false);

      if (!currentData || !Array.isArray(currentData.rows)) {
        return;
      }

      let dataRow = currentData.rows.find((item) => item.id == value);
      if (!dataRow) {
        return;
      }

      dataRow.selected = extra;
      let rows = selectedRows ? [...selectedRows] : [];
      let rowExist = rows.findIndex((item) => item.id == value);

      if (rowExist > -1 && !extra) {
        rows.splice(rowExist, 1);
      } else if (rowExist === -1 && extra) {
        rows.push(dataRow);
      }

      setSelectedRows(rows);
      if (rows.length > 0 && settlements.length > 0) setUpdateEnabled(true);

      setHiddenColumns(DEFAULT_HIDDEN_COLUMNS);
      setDataTableData((previousData) => ({ ...previousData, rows: [...currentData.rows] }));
    }

    if (key == "selectAll") {
      setUpdateEnabled(false);
      let dataRows = currentData.rows;
      if (extra == "all") {
        dataRows = value;
        for (let index = 0; index < dataRows.length; index++) {
          const dataRow = dataRows[index];
          dataRow.selected = true;
        }
        setSelectedRows(dataRows);
      } else if (extra == "none") {
        dataRows = value;
        for (let index = 0; index < dataRows.length; index++) {
          const dataRow = dataRows[index];
          dataRow.selected = false;
        }
        setSelectedRows([]);
      }
      if (dataRows.filter((dr) => dr.selected).length > 0 && settlements.length > 0)
        setUpdateEnabled(true);

      setHiddenColumns(DEFAULT_HIDDEN_COLUMNS);
      setDataTableData((previousData) => ({ ...previousData, rows: [...currentData.rows] }));
    }
  };

  async function fetchFixtures(clearTable) {
    setLoading(true);
    setFixturesLoaded(false);
    getSettlementConfig();

    const [mutedIdsMap, blockedIdsMap] = await Promise.all([
      fetchMutedFixtures(),
      fetchBlockedFixtures(),
    ]);

    let getFixturesEndpoint = `/api/v2/panel/settlements/unsettled/sports/1000/outrights`;

    let response = await getData(
      getFixturesEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortControllerRef.current ? { signal: abortControllerRef.current.signal } : null
    );
    if (response != null) {
      let dataTable = { ...dataTableData };
      if (clearTable) {
        dataTable.rows = [];
        currentData = [];
        allTableData = [];
      }

      let mutedFixtures = [];
      let unmutedFixtures = [];

      for (let index = 0; index < response.data.length; index++) {
        const event = response.data[index];
        event.tm = Date.now();
        event.selected = false;

        let minFinished = null;
        let hours = null;
        let minutes = null;
        let timestamp =
          event.status === "In progress" || event.status === "Not started yet"
            ? event.start_date
            : event.finished_at;
        if (timestamp) {
          let now = new Date();
          let finished_at = new Date(timestamp * 1);
          minFinished = now.getTime() - finished_at.getTime();
          minFinished = Math.round(minFinished / 1000 / 60);
          hours = Math.floor(minFinished / 60);
          minutes = minFinished - hours * 60;
          hours = hours < 10 ? "0" + hours : hours;
          minutes = minutes < 10 ? "0" + minutes : minutes;
        }

        event.provider_id = event.uni_id ? 1000 : event.provider_id;
        const sportKey = event.sport ? event.sport.toLowerCase().replace(/ /g, "") : "";
        const timeExceededLimit = timeExceededRules[sportKey];
        event.time_exceeded = Boolean(
          hours && timeExceededLimit && Number(hours) >= timeExceededLimit
        );
        event.fixture_id = event.uni_id ? event.uni_id : event.event_id;
        if (event.id === undefined || event.id === null || event.id === "") {
          const providerKey = event.provider_id || event.provider || "unknown";
          event.id = `${providerKey}:${event.fixture_id}:${index}`;
        }
        event.blocked = blockedIdsMap[`${event.provider_id}:${event.fixture_id}`];
        event.matched = Boolean(event.uni_id);
        event.payment_percentage = "";
        event.unconfirmed_outcome_id = 0;
        event.outrightName = `${event.sport} - ${event.region} - ${event.competition} - ${event.home} - ${event.market_name}`;

        if (event.matched) {
          if (mutedIdsMap["1000:" + event.fixture_id]) {
            mutedFixtures.push(event);
          } else {
            unmutedFixtures.push(event);
          }
        } else {
          if (mutedIdsMap[event.provider + ":" + event.fixture_id]) {
            mutedFixtures.push(event);
          } else {
            unmutedFixtures.push(event);
          }
        }
      }

      dataTable.rows = response.data;

      allTableData = { ...dataTable };
      currentData = { ...dataTable };

      setMutedFixtures({});
      setMutedFixtures({ columns: dataTable.columns, rows: mutedFixtures });
      setUnmutedFixtures({});
      setUnmutedFixtures({ columns: dataTable.columns, rows: unmutedFixtures });

      setFixturesLoaded(true);
      setLoading(false);
    }
  }

  async function fetchMutedFixtures() {
    let getMutedFixturesEndpoint = `/api/v1/bets/mutedFixtures`;

    let mutedIdsloaded = [];
    setMutedFixturesLoaded(false);
    let response = await getData(
      getMutedFixturesEndpoint,
      {
        Authorization: getAuthorizationHeader(),
      },
      null,
      abortControllerRef.current ? { signal: abortControllerRef.current.signal } : null
    );
    if (response != null) {
      mutedIdsloaded = response.data.map((r) => {
        return r.event_key;
      });
    }

    const mutedIdsDictionary = {};
    for (let index = 0; index < mutedIdsloaded.length; index++) {
      const eventKey = mutedIdsloaded[index];
      mutedIdsDictionary[eventKey] = eventKey;
    }

    setMutedFixturesLoaded(true);
    return mutedIdsDictionary;
  }

  async function fetchBlockedFixtures() {
    let getBlockedFixturesEndpoint = `/api/v2/panel/filters/fixtures/blocked`;
    let blockedIdsloaded = [];
    let response = await getData(
      getBlockedFixturesEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortControllerRef.current ? { signal: abortControllerRef.current.signal } : null
    );

    if (response != null) {
      blockedIdsloaded = response.data.map((r) => {
        return `${r.provider_id}:${r.fixture_id}::${r.status == "blocked" ? true : false}`;
      });
    }

    const blockedIdsDictionary = {};
    for (let index = 0; index < blockedIdsloaded.length; index++) {
      const blockedItem = blockedIdsloaded[index].split("::");
      blockedIdsDictionary[blockedItem[0]] = blockedItem[1];
    }

    return blockedIdsDictionary;
  }

  async function mutedSwitch(e, mute) {
    if (e && e.preventDefault) e.preventDefault();
    setAllSelected(false);
    setUpdateEnabled(false);
    setOutrightSelected(false);

    for (let index = 0; index < currentData.rows.length; index++) {
      const dataRow = currentData.rows[index];
      dataRow.selected = false;
    }

    setSelectedRows([]);
    if (mute) {
      currentData = mutedFixtures ? { ...mutedFixtures } : currentData;
    } else {
      currentData = unmutedFixtures ? { ...unmutedFixtures } : currentData;
    }

    changeSorting(currentData);
    setMutedSelected(mute);
    setHiddenColumns(DEFAULT_HIDDEN_COLUMNS);
  }

  async function allSwitch(e, all) {
    if (e && e.preventDefault) e.preventDefault();
    setAllSelected(all);
    setSelectedRows([]);
    setUpdateEnabled(false);
    setOutrightSelected(false);

    for (let index = 0; index < currentData.rows.length; index++) {
      const dataRow = currentData.rows[index];
      dataRow.selected = false;
    }

    currentData = { ...allTableData };
    changeSorting(currentData);
    setHiddenColumns(DEFAULT_HIDDEN_COLUMNS);
    // setDataTableData((previousData) => ({ ...previousData, rows: [...(currentData.rows || [])] }));
  }

  async function getSettlementConfig() {
    let getSettlementsConfig = `/api/v1/bets/settlements/config`;
    let settlementConfigResult = await getData(
      getSettlementsConfig,
      {
        Authorization: getAuthorizationHeader(),
      },
      null,
      abortControllerRef.current ? { signal: abortControllerRef.current.signal } : null
    );
    if (settlementConfigResult != null) {
      setWorkflow(settlementConfigResult.data[0].status);
    }
  }

  async function setSettlementConfig(status) {
    let getSettlementsConfig = `/api/v1/bets/settlements/config`;
    let settlementConfigResult = await postData(
      getSettlementsConfig,
      { functionality: "confirmation", status: status },
      {
        Authorization: getAuthorizationHeader(),
      },
      null,
      abortControllerRef.current ? { signal: abortControllerRef.current.signal } : null
    );
    if (settlementConfigResult != null) {
      setWorkflow(settlementConfigResult.data[0].status);
    }
  }

  function checkedDiffs({ ...rest }) {
    return checkedFixtures[rest.row.original.fixture_id];
  }

  function uncheckedDiffs({ ...rest }) {
    return uncheckedFixtures[rest.row.original.fixture_id];
  }

  function renderDate({ value, ...rest }) {
    let dateformatted = formatDate(value);
    return (
      <div style={{ maxWidth: "100px", overflowWrap: "break-word" }}>
        <span>{dateformatted}</span>{" "}
        <HtmlTooltip
          className="white-html-tooltip"
          placement="right"
          title={
            <React.Fragment>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  maxWidth: "200px",
                  flexDirection: "columns",
                }}
              >
                <div style={{ width: "max-content" }}>StartDates Info:</div>
                {rest.row.original.matched ? (
                  rest.row.original.start_dates.map((date, index) => {
                    return (
                      <div
                        key={date.pid + "-" + index}
                        style={{ display: "flex", width: "fit-content" }}
                      >
                        <div style={{ display: "flex", width: "fit-content" }}>
                          <span className={"provider-" + date.pid}>{date.pid}: </span>
                          {"  "}
                          <span>{formatDate(date.val)}</span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ display: "flex", width: "fit-content" }}>
                    <div style={{ display: "flex", width: "fit-content" }}>
                      <span className={"provider-" + rest.row.original.provider}>
                        {rest.row.original.provider}:{" "}
                      </span>
                      {"  "}
                      <span>{dateformatted}</span>
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          }
        >
          <span
            style={{
              display:
                rest.row.original.start_dates && rest.row.original.start_dates.length > 1
                  ? ""
                  : "none",
              cursor: "pointer",
            }}
            className="matched"
          >
            {" "}
            *
          </span>
        </HtmlTooltip>
      </div>
    );
  }

  function formatDate(value) {
    let date = new Date(value * 1);
    let day = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
    let month = date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
    let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
    let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();

    let dateformatted = day + "/" + month + "/" + date.getFullYear() + " " + hours + ":" + minutes;
    let timezone = (date.getTimezoneOffset() * -1) / 60;
    timezone = timezone > 0 ? "+" + timezone : timezone;
    return dateformatted;
  }

  function openAdvancedSearch(e, data) {
    e.preventDefault();

    window.open("/settlement/advanced?" + data[0] + "=" + data[1] + "&" + data[2] + "=" + data[3]);
  }

  function matchedRender({ value, ...rest }) {
    let matched = rest.row.original.matched;
    let linked = rest.row.original.linked;

    return (
      <Grid className="cur-stlmnt-actions" sx={{ width: "100%", textAlign: "right !important" }}>
        <Grid>
          <div className={matched ? "outrights-matched" : "outrights-unmatched"}>
            <Button
              className={matched ? "outrights-matched" : "outrights-unmatched"}
              onClick={(e) =>
                openAdvancedSearch(
                  e,
                  matched
                    ? ["uni_id", value, "sport_id", rest.row.original.sport_id]
                    : ["provider_id", value, "sport_id", rest.row.original.sport_id]
                )
              }
            >
              {value}
            </Button>
            <Icon
              style={{ cursor: "pointer" }}
              className="info-icon"
              onClick={() => handleUUIDCopy(value)}
            >
              copy
            </Icon>

            <HtmlTooltip
              className="white-html-tooltip"
              placement="right"
              title={
                <React.Fragment>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      maxWidth: "200px",
                      flexDirection: "columns",
                      backgroundColor: "#e9ecef",
                      color: "#000",
                      borderColor: "#e9ecef",
                    }}
                  >
                    Linked EventId: {linked}
                  </div>
                </React.Fragment>
              }
            >
              <Button style={{ display: linked ? "" : "none" }} className={linked ? "linked" : ""}>
                <Icon>link_icon</Icon>
              </Button>
            </HtmlTooltip>
          </div>
        </Grid>
      </Grid>
    );
  }

  function formatStatus({ value, ...rest }) {
    return (
      <>
        <div className={value.replace(/ /g, "_").toLowerCase()}>
          <span>{value}</span>
          <HtmlTooltip
            className="white-html-tooltip"
            placement="right"
            title={
              <React.Fragment>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    maxWidth: "200px",
                    flexDirection: "columns",
                    backgroundColor: "#e9ecef",
                    color: "#000",
                    borderColor: "#e9ecef",
                  }}
                >
                  <div style={{ width: "100%" }}>Status Info:</div>
                  {rest.row.original.matched ? (
                    rest.row.original.statuses.map((status, index) => {
                      return (
                        <div
                          key={status.pid + "-" + index}
                          style={{ display: "flex", width: "fit-content" }}
                        >
                          <div style={{ display: "flex", width: "fit-content" }}>
                            <span className={"provider-" + status.pid}>{provider.provider}: </span>
                            {"  "}
                            <span className={status.val.replace(/ /g, "_").toLowerCase()}>
                              {status.val}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ display: "flex", width: "fit-content" }}>
                      <div style={{ display: "flex", width: "fit-content" }}>
                        <span className={"provider-" + rest.row.original.provider}>
                          {rest.row.original.provider}:{" "}
                        </span>
                        {"  "}
                        <span className={rest.row.original.status.replace(/ /g, "_").toLowerCase()}>
                          {rest.row.original.status}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            }
          >
            <span
              style={{
                display:
                  rest.row.original.statuses && rest.row.original.statuses.length > 1 ? "" : "none",
                cursor: "pointer",
              }}
              className="matched"
            >
              {" "}
              *
            </span>
          </HtmlTooltip>
        </div>
      </>
    );
  }

  const handleConfirmWorkflow = (e) => {
    e.preventDefault();

    let confirmation_config = !workflow;
    setWorkflow(confirmation_config);

    setSettlementConfig(confirmation_config);
  };

  const handleMuteUnMute = (e, status) => {
    e.preventDefault();
    if (selectedRows.length > 0) {
      let action = status ? "mute" : "unmute";
      Swal.fire({
        title: `Confirmation`,
        html: `You are going to ${action} </br></br>
            ${selectedRows.length} 
            Selected Fixtures. </br></br> Are you Sure?`,
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: `Yes, ${action} them!`,
        denyButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#87cb16",
        denyButtonColor: "#ff4a55",
      }).then(async (result) => {
        if (result.isConfirmed) {
          let mutedFixtures = selectedRows.map((f) => {
            if (f.matched) {
              return "1000:" + f.fixture_id;
            } else {
              return f.provider + ":" + f.fixture_id;
            }
          });

          let setMuteFixtures = status
            ? `/api/v1/bets/mutedFixtures`
            : `/api/v1/bets/unmuteFixtures`;
          let mutedFixturesResult = await postData(setMuteFixtures, mutedFixtures, {
            Authorization: getAuthorizationHeader(),
          });

          if (mutedFixturesResult != null) {
            Swal.fire("Request Sent!", `Fixtures mute/unmute Sent to system.`, "success").then(
              async () => {
                setUpdateEnabled(true);
                setUpdateEnabled(false);

                settlements = [];
                setSelectedRows([]);
                if (autoRefresh) fetchFixtures(true);
              }
            );
          } else {
            Swal.fire(
              "Error!",
              `Something went wrong while triyng to mute/unmute Fixtures.`,
              "error"
            ).then(async () => {
              setUpdateEnabled(true);
              setUpdateEnabled(false);
              settlements = [];
              setSelectedRows([]);
              fetchFixtures(true);
            });
          }
        }
      });
    } else {
      //console.log("Nothing have been changed");
    }
  };

  const handleClose = (...args) => {
    const reason = args[1];
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  /**
   *
   */
  const handleUUIDCopy = (UUID) => {
    copyToClipboard(UUID);
  };

  const copyToClipboard = (textToCopy) => {
    setApiresponse("success");
    setApiResponseMsg("Value has been copied to the clipboard!");
    setNotification(true);
    // navigator clipboard api needs a secure context (https)
    if (navigator.clipboard && window.isSecureContext) {
      // navigator clipboard api method'
      return navigator.clipboard.writeText(textToCopy);
    } else {
      // text area method
      let textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      // make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      return new Promise((res, rej) => {
        // here the magic happens
        document.execCommand("copy") ? res() : rej();
        textArea.remove();
      });
    }
  };

  useEffect(() => {
    changeSorting();
  }, [sortByUnchecked]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SORT_BY_UNCHECKED_STORAGE_KEY,
        sortByUnchecked ? 'true' : 'false'
      );
    } catch (error) {
      console.error('Unable to remember outrights settlement sort preference', error);
    }
  }, [sortByUnchecked]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (
      permissions.length > 0 &&
      (permissions.indexOf("settlement-manager") > -1 || permissions.indexOf("super") > -1)
    ) {
      setSettlementManager(true);
    }
  }, [permissions]);

  useEffect(() => {
    if (fixturesLoaded && mutedFixturesLoaded) {
      if (mutedSelected) mutedSwitch(null, true);
      if (allSelected) allSwitch(null, true);
      if (!mutedSelected && !outrightSelected && !allSelected) mutedSwitch(null, false);
    }
  }, [fixturesLoaded, mutedFixturesLoaded, mutedSelected, allSelected, outrightSelected]);

  useEffect(() => {
    if (providers) fetchFixtures(true);
  }, [providers]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <EditSettlement
        showModal={showEditModal}
        signalCloseModal={handleEditCloseModal}
        data={rowDataSelected}
      ></EditSettlement>
      <SoftBox my={3} sx={{ marginBottom: "0px" }}>
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          p={3}
          sx={{ paddingTop: "0px", paddingRight: "0px" }}
        >
          <SoftBox mt={0} className="settlement-page-holder">
            <Grid container spacing={3}>
              <Grid item xs={12} className="scrolling-main-page-settlement_">
                <SoftBox mb={3}>
                  <Grid container spacing={0}>
                    <Card style={{ width: "100%" }}>
                      <Grid item xs={12} sx={{ paddingTop: "0px", height: "100%" }}>
                        <SoftBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          pt={2}
                          pl={3}
                          pr={3}
                        >
                          <SoftBox lineHeight={1}>
                            <SoftTypography variant="h5" fontWeight="medium">
                              Outrights
                            </SoftTypography>
                            <SoftTypography variant="button" fontWeight="regular" color="text">
                              Here you can find all the <b>current</b>{" "}
                              <span style={{ color: "#cb0c9f" }}>Unsettled Outright bets</span> for
                              each Fixture
                            </SoftTypography>
                            <Button
                              className={sortByUnchecked ? "sort" : "unsort"}
                              onClick={setSortBy}
                            >
                              {sortByUnchecked ? "Sort by New Date - ON" : "Sort by New Date - OFF"}
                            </Button>
                          </SoftBox>
                          <SoftBox sx={{ display: settlementManager ? "" : "none" }}>
                            <ToggleButtonGroup
                              value={workflow}
                              exclusive
                              onChange={handleConfirmWorkflow}
                              aria-label="text alignment"
                            >
                              <ToggleButton
                                className={
                                  workflow == undefined
                                    ? "workflow_undefined"
                                    : workflow
                                    ? "workflow_enabled"
                                    : "workflow_disabled"
                                }
                                value={true}
                                aria-label="Enable/Disable Confirmation Workflow"
                              >
                                {workflow == undefined
                                  ? "Confirmation Workflow"
                                  : workflow
                                  ? "Confirmation Workflow - ON"
                                  : "Confirmation Workflow - OFF"}
                              </ToggleButton>
                            </ToggleButtonGroup>
                            <Button
                              className={autoRefresh ? "sort" : "unsort"}
                              onClick={setAutorefreshValue}
                            >
                              {autoRefresh ? "AutoRefresh - ON" : "AutoRefresh - OFF"}
                            </Button>
                          </SoftBox>
                        </SoftBox>
                        <SoftBox
                          p={3}
                          sx={{
                            paddingTop: "0px !important",
                            paddingBottom: "0px !important",
                            zIndex: 3,
                          }}
                        >
                          <Grid container>
                            <Grid item xs={6} md={6} style={{ textAlign: "left" }}>
                              <Button
                                className={
                                  allSelected || outrightSelected
                                    ? "switch-unmuted"
                                    : mutedSelected
                                    ? "switch-unmuted"
                                    : "switch-muted"
                                }
                                onClick={(e) => mutedSwitch(e, false)}
                              >
                                Unmuted (
                                {unmutedFixtures && unmutedFixtures.rows
                                  ? unmutedFixtures.rows.length
                                  : 0}
                                )
                              </Button>
                              <Button
                                className={
                                  allSelected || outrightSelected
                                    ? "switch-unmuted"
                                    : mutedSelected
                                    ? "switch-muted"
                                    : "switch-unmuted"
                                }
                                onClick={(e) => mutedSwitch(e, true)}
                              >
                                Muted (
                                {mutedFixtures && mutedFixtures.rows
                                  ? mutedFixtures.rows.length
                                  : 0}
                                )
                              </Button>
                              <Button
                                className={allSelected ? "switch-muted" : "switch-unmuted"}
                                onClick={(e) => allSwitch(e, true)}
                              >
                                All
                              </Button>
                            </Grid>
                            <Grid item xs={6} md={6} style={{ textAlign: "right" }}>
                              <SoftBox
                                lineHeight={1}
                                sx={{ display: oddSettingsEditCached ? "" : "none" }}
                              >
                                <Button
                                  onClick={(e) => handleMuteUnMute(e, true)}
                                  disabled={
                                    allSelected
                                      ? !selectedRows || selectedRows.length == 0
                                      : mutedSelected
                                      ? true
                                      : !selectedRows || selectedRows.length == 0
                                  }
                                >
                                  Mute
                                </Button>
                                <Button
                                  onClick={(e) => handleMuteUnMute(e, false)}
                                  disabled={
                                    allSelected
                                      ? !selectedRows || selectedRows.length == 0
                                      : mutedSelected
                                      ? !selectedRows || selectedRows.length == 0
                                      : true
                                  }
                                >
                                  Unmute
                                </Button>
                              </SoftBox>
                            </Grid>
                          </Grid>
                        </SoftBox>

                        <DataTable
                          table={dataTableData}
                          tableClassName="outrights-settlement-table"
                          bodyClassName="settlement-fixtures-list"
                          entriesPerPage={{
                            defaultValue: 10,
                            entries: [5, 10, 15, 20, 25, 30],
                          }}
                          hiddenColumns={hiddenColumns}
                          autoResetPageDefault={false}
                          autoResetGlobalFilter={true}
                          autoResetSortByDefault={false}
                          canSearch
                          refreshEnabled={true}
                          openBackDrop={loading}
                          checkboxSelection={oddSettingsEditCached}
                          informParent={handleTableDataChanges}
                        ></DataTable>
                      </Grid>
                    </Card>
                  </Grid>
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
}

export default OutrightSettlement;
